// src/pages/vendor/Dashboard.js
import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import InventoryIcon from "@mui/icons-material/Inventory";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PolicyIcon from '@mui/icons-material/Policy';
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import WarningIcon from "@mui/icons-material/Warning";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import socket from "../../socket";
import {
  requestNotificationPermission,
  getNotificationPermission,
  registerServiceWorker,
  subscribeToPush,
  getPushEnabled,
  setPushEnabled,
  getNotificationStatus,
  reRegisterServiceWorker,
  showBrowserNotification,
} from "../../utils/pushNotification";

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Pie,
  PieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import LoadingScreen from "../../components/LoadingScreen";
const filterAnimStyles = `
@keyframes shake {
  0%, 100% { transform: rotate(0deg); }
  10%, 30%, 50%, 70%, 90% { transform: rotate(-12deg); }
  20%, 40%, 60%, 80% { transform: rotate(12deg); }
}
`;

const CARD_ACCENTS = {
  products:  { icon: '#ff9900', bg: 'rgba(255,153,0,0.10)',  border: '#fff3e0' },
  orders:    { icon: '#1976d2', bg: 'rgba(25,118,210,0.10)',  border: '#e3eeff' },
  revenue:   { icon: '#16a34a', bg: 'rgba(22,163,74,0.10)',   border: '#dcfce7' },
  lowStock:  { icon: '#ed6c02', bg: 'rgba(237,108,2,0.10)',   border: '#ffe0b2' },
  questions: { icon: '#7c3aed', bg: 'rgba(124,58,237,0.10)',  border: '#ede9fe' },
  recycleBin: { icon: '#546e7a', bg: 'rgba(84,110,122,0.10)', border: '#cfd8dc' },
};

const VendorDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Get user greeting name from localStorage (same pattern as Agent)
  const [vendorName, setVendorName] = useState(() => {
    const vp = JSON.parse(localStorage.getItem('vendorProfile') || '{}');
    if (vp?.name && !vp.name.includes('@')) return vp.name;
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const raw = u?.name || u?.firstName || u?.username || '';
    return raw.includes('@') ? '' : raw;
  });

  const [summary, setSummary] = useState({
  totalProducts: 0,
  totalOrders: 0,
  totalRevenue: 0,
  lowStockCount: 0,
  outOfStockCount: 0,
});

const [analytics, setAnalytics] = useState({
  monthlySales: [],
  weeklyRevenue: [],
  topProducts: [],
});

const [unansweredQuestionsCount, setUnansweredQuestionsCount] = useState(0);
const [hasNewOrder, setHasNewOrder] = useState(false);
const [loading, setLoading] = useState(true);
const [pushPermission, setPushPermission] = useState(() => getNotificationPermission());
const [pushEnabled, setPushEnabledState] = useState(() => getPushEnabled());
const [notifStatus, setNotifStatus] = useState(null);
const [notifChecking, setNotifChecking] = useState(false);

const fetchSummary = useCallback(async () => {
  try {
    const { data } = await api.get("vendor/dashboard/summary");
    setSummary(data);
    if (data?.vendorName) setVendorName(data.vendorName);
    setHasNewOrder(data.hasPendingOrders === true);
  } catch (err) {
    console.error("Dashboard summary failed", err);
  }
}, []);

const fetchAnalytics = useCallback(async () => {
  try {
    const { data } = await api.get("vendor/dashboard/analytics");
    setAnalytics(data);
  } catch (err) {
    console.error("Analytics failed", err);
  }
}, []);

const fetchUnansweredQuestionsCount = useCallback(async () => {
  try {
    const { data } = await api.get("/vendor/dashboard/unanswered-questions-count");
    setUnansweredQuestionsCount(data.count || 0);
  } catch (err) {
    console.error("Failed to fetch unanswered questions count", err);
  }
}, []);

const refreshDashboardData = useCallback(async () => {
  await Promise.all([fetchSummary(), fetchUnansweredQuestionsCount()]);
}, [fetchSummary, fetchUnansweredQuestionsCount]);

const loadDashboardData = useCallback(async () => {
  setLoading(true);
  try {
    await Promise.all([fetchSummary(), fetchAnalytics(), fetchUnansweredQuestionsCount()]);
  } finally {
    setLoading(false);
  }
}, [fetchSummary, fetchAnalytics, fetchUnansweredQuestionsCount]);

  /* Keep refs to avoid socket dependency re-run */
  const refreshRef = useRef(refreshDashboardData);
  const fetchSummaryRef = useRef(fetchSummary);
  const fetchUnansweredRef = useRef(fetchUnansweredQuestionsCount);
  useEffect(() => { refreshRef.current = refreshDashboardData; }, [refreshDashboardData]);
  useEffect(() => { fetchSummaryRef.current = fetchSummary; }, [fetchSummary]);
  useEffect(() => { fetchUnansweredRef.current = fetchUnansweredQuestionsCount; }, [fetchUnansweredQuestionsCount]);

  useEffect(() => {
    void loadDashboardData();
  }, [loadDashboardData]);

  /* Socket connection and event listeners - runs once on mount */
  useEffect(() => {
    void requestNotificationPermission();
    // Check backend if user actually has a push subscription — reset localStorage if not
    (async () => {
      try {
        const { data } = await api.get('/push/status');
        let browserHasSub = false;
        if ('serviceWorker' in navigator) {
          try {
            const reg = await navigator.serviceWorker.getRegistration('/');
            if (reg) {
              const sub = await reg.pushManager.getSubscription();
              browserHasSub = !!sub;
            }
          } catch (e) {}
        }
        const isSubscribed = data.subscribed && browserHasSub;
        setPushEnabledState(isSubscribed);
        setPushEnabled(isSubscribed);
      } catch (e) {
        // ignore
      }
    })();
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const storedVendorProfile = JSON.parse(localStorage.getItem('vendorProfile') || '{}');
    const vendorRoomId = storedVendorProfile?._id || storedUser?.vendorId || storedUser?._id;
    if (!vendorRoomId) return;

    socket.emit('vendor_join', vendorRoomId);
    const userRoomId = storedUser?._id;
    if (userRoomId && userRoomId !== vendorRoomId) {
      socket.emit('vendor_join', userRoomId);
    }

    const handleConnect = () => {
      socket.emit('vendor_join', vendorRoomId);
      if (userRoomId && userRoomId !== vendorRoomId) {
        socket.emit('vendor_join', userRoomId);
      }
    };

    /* Listen for new order notifications - update count + show bell + browser notification */
    const handleNewOrder = () => {
      setHasNewOrder(true);
      if (refreshRef.current) refreshRef.current();
      /* Trigger app:play-bell for sound (handled by NotificationManager) + show notification */
      window.dispatchEvent(new CustomEvent('app:play-bell'));
      /* Show notification using native Notification API (most reliable, works in background) */
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const n = new Notification('New Order Received', {
            body: 'A new order has been assigned to you.',
            icon: '/logo192.png',
            tag: 'vendor-order-' + Date.now(),
            requireInteraction: true,
          });
          n.onclick = () => { window.focus(); window.location.href = '/vendor/orders'; };
        } catch (e) { /* ignore */ }
      }
    };

    socket.on('connect', handleConnect);
    socket.on('new_order_notification', handleNewOrder);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('new_order_notification', handleNewOrder);
    };
  }, []);

useEffect(() => {
  const handleVisibility = () => { if (document.visibilityState === 'visible') setPushPermission(getNotificationPermission()); };
  document.addEventListener('visibilitychange', handleVisibility);
  return () => document.removeEventListener('visibilitychange', handleVisibility);
}, []);

  const handleEnablePushNotifications = useCallback(async () => {
    const result = await requestNotificationPermission();
    setPushPermission(result);
    if (result === 'granted') {
      await registerServiceWorker();
      const sub = await subscribeToPush();
      if (sub) {
        setPushEnabled(true); setPushEnabledState(true);
      }
    } else if (result === 'denied') {
      console.warn('Notification permission denied');
    }
  }, []);

  const handleDisablePushNotifications = useCallback(async () => {
    setPushEnabled(false); setPushEnabledState(false);
    // Also try to unsubscribe from the browser's push manager
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration('/');
        if (reg) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) await sub.unsubscribe();
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

const currencyFormatter = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const cards = [
  {
    key: "products",
    icon: <InventoryIcon />,
    title: `${summary.totalProducts} Products`,
    subtitle: "Active in your shop",
    onClick: () => navigate("/vendor/products?type=all"),
  },
  {
    key: "orders",
    icon: <ReceiptLongIcon />,
    title: `${summary.totalOrders} Orders`,
    subtitle: "Total orders received",
    onClick: () => navigate("/vendor/orders"),
  },
  {
    key: "revenue",
    icon: <TrendingUpIcon />,
    title: `₹${Number(summary.totalRevenue || 0).toLocaleString("en-IN")}`,
    subtitle: "Total revenue",
  },
  {
    key: "lowStock",
    icon: <WarningIcon />,
    title: `${(summary.lowStockCount || 0) + (summary.outOfStockCount || 0)} Low & Out of Stock`,
    subtitle: "Products needing attention",
    onClick: () => navigate("/vendor/products?filter=low+out"),
  },
  {
    key: "recycleBin",
    icon: <InventoryIcon />,
    title: "Recycle Bin",
    subtitle: "View deleted / archived items",
    onClick: () => navigate("/vendor/products/archived"),
  },
  {
    key: "questions",
    icon: <QuestionAnswerIcon />,
    title: `${unansweredQuestionsCount} Unanswered`,
    subtitle: "Questions need your response",
    onClick: () => navigate("/vendor/unanswered-questions"),
  },
];

const COLORS = ["#ff9900", "#1976d2", "#4caf50", "#e53935", "#7c3aed", "#f59e0b", "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#8b5cf6", "#22c55e"];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <style>{filterAnimStyles}</style>
      {/* ================= HEADER (matches Agent Dashboard) ================= */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', flexGrow: 1 }}>
            {(() => { const hour = new Date().getHours(); const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'; const fullName = vendorName; const name = fullName.split(' ')[0] || 'Vendor'; return `👋 ${greeting}, ${name}!`; })()}
          </Typography>

          {/* ── Push Notification Toggle (matches Agent Dashboard) ── */}
          {(() => {
            if (pushPermission === 'denied') {
              return (
                <Button variant="outlined" size="small" disabled title="Enable in browser settings → Site Settings → Notifications" sx={{ color: '#ef5350', borderColor: '#ef5350', textTransform: 'none', borderRadius: '20px', fontSize: '0.78rem', opacity: 0.8 }}>
                  🔕 Notifications Blocked
                </Button>
              );
            }
            if (pushPermission === 'default' || !pushEnabled) {
              return (
                <Button variant="outlined" size="small" onClick={handleEnablePushNotifications} disabled={notifChecking} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.35)', textTransform: 'none', borderRadius: '20px', fontSize: '0.78rem', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                  {notifChecking ? <CircularProgress size={14} sx={{ color: '#fff', mr: 0.5 }} /> : '🔔 '}
                  {notifChecking ? 'Checking...' : pushPermission === 'default' ? 'Enable Notifications' : 'Notifications Off'}
                </Button>
              );
            }
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={handleDisablePushNotifications} sx={{ color: '#4ade80', borderColor: '#4ade80', textTransform: 'none', borderRadius: '20px', fontSize: '0.78rem', '&:hover': { borderColor: '#22c55e', bgcolor: 'rgba(74,222,128,0.08)' } }}>
                  ✅ Notifications On
                </Button>
                {notifStatus && !notifStatus.working && notifStatus.actionable && (
                  <Button variant="outlined" size="small" onClick={async () => {
                    setNotifChecking(true);
                    await reRegisterServiceWorker();
                    const status = await getNotificationStatus();
                    setNotifStatus(status);
                    setNotifChecking(false);
                  }} disabled={notifChecking} sx={{ color: '#ff9800', borderColor: '#ff9800', textTransform: 'none', borderRadius: '20px', fontSize: '0.7rem', '&:hover': { borderColor: '#f57c00', bgcolor: 'rgba(255,152,0,0.08)' } }}>
                    {notifChecking ? <CircularProgress size={14} sx={{ color: '#ff9800' }} /> : '⚠️ Fix Notifications'}
                  </Button>
                )}
              </Box>
            );
          })()}
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Manage your products, orders, and store performance.</Typography>
      </Box>

      {loading ? (
        <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
          <LoadingScreen message="Loading your dashboard..." />
        </Box>
      ) : (
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
        {/* ================= TOP SECTION: Orders & Revenue ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#16a34a' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Orders & Revenue
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(2, 1fr)',
            },
            alignItems: 'stretch',
            mb: 4,
          }}
        >
          {cards.filter(c => c.key === 'orders' || c.key === 'revenue').map((c) => {
            const accent = CARD_ACCENTS[c.key] || CARD_ACCENTS.products;
            return (
              <Box
                key={c.key}
                onClick={c.onClick}
                role={c.onClick ? 'button' : undefined}
                tabIndex={c.onClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (c.onClick && (e.key === 'Enter' || e.key === ' ')) c.onClick();
                }}
                sx={{
                  p: 1.25,
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  border: `1.5px solid ${accent.border}`,
                  boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
                  cursor: c.onClick ? 'pointer' : 'default',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  minHeight: { xs: 80, sm: 80, md: 90 },
                  '&:hover': c.onClick
                    ? { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' }
                    : {},
                }}
              >
                <Box
                  sx={{
                    width: { xs: 34, sm: 38, lg: 36 },
                    height: { xs: 34, sm: 38, lg: 36 },
                    borderRadius: '10px',
                    bgcolor: accent.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  {React.cloneElement(c.icon, {
                    sx: { fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon },
                  })}
                  {c.key === 'orders' && hasNewOrder && (
                    <NotificationsActiveIcon
                      sx={{
                        position: 'absolute',
                        top: -6,
                        right: -6,
                        fontSize: 16,
                        color: '#e53935',
                        animation: 'shake 0.6s ease-in-out infinite',
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.7rem', lg: '0.68rem' },
                      fontWeight: 500,
                      color: '#64748b',
                      lineHeight: 1.2,
                      mb: 0.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {c.subtitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.1rem', sm: '1.2rem', lg: '1rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                      color: '#0f172a',
                    }}
                  >
                    {c.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ================= SECOND SECTION: Products & Alerts ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Products & Alerts
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            alignItems: 'stretch',
          }}
        >
          {cards.filter(c => c.key !== 'orders' && c.key !== 'revenue').map((c) => {
            const accent = CARD_ACCENTS[c.key] || CARD_ACCENTS.products;
            return (
              <Box
                key={c.key}
                onClick={c.onClick}
                role={c.onClick ? 'button' : undefined}
                tabIndex={c.onClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (c.onClick && (e.key === 'Enter' || e.key === ' ')) c.onClick();
                }}
                sx={{
                  p: 1.25,
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  border: `1.5px solid ${accent.border}`,
                  boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
                  cursor: c.onClick ? 'pointer' : 'default',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  minHeight: { xs: 80, sm: 80, md: 90 },
                  '&:hover': c.onClick
                    ? { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' }
                    : {},
                }}
              >
                <Box
                  sx={{
                    width: { xs: 34, sm: 38, lg: 36 },
                    height: { xs: 34, sm: 38, lg: 36 },
                    borderRadius: '10px',
                    bgcolor: accent.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {React.cloneElement(c.icon, {
                    sx: { fontSize: { xs: 18, sm: 20, lg: 18 }, color: accent.icon },
                  })}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.7rem', lg: '0.68rem' },
                      fontWeight: 500,
                      color: '#64748b',
                      lineHeight: 1.2,
                      mb: 0.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {c.subtitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.1rem', sm: '1.2rem', lg: '1rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                      color: '#0f172a',
                    }}
                  >
                    {c.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ================= MANAGE STORE ================= */}
        <Box mt={5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
              Manage Store
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(6, 1fr)',
              },
              alignItems: 'stretch',
            }}
          >
            {[
              {
                icon: <AddBusinessIcon />,
                title: "Add New Product",
                desc: "Add fresh products to your catalog",
                action: "/vendor/products/new",
                label: "Add Product",
              },
              {
                icon: <InventoryIcon />,
                title: "View All Products",
                desc: "Manage & edit your product list",
                action: "/vendor/products?type=all",
                label: "My Products",
              },
               {
                icon: <PolicyIcon sx={{ color: '#000000' }} />,
                title: "Store Policies",
                desc: "View & manage your store policies",
                action: "/vendor/policies",
                label: "View Policies",
              }
            ].map((c, i) => (
              <Card key={i} sx={{
                borderRadius: 3,
                boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
                p: 2,
                border: '1.5px solid #f1f5f9',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: 'rgba(25,118,210,0.10)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(c.icon, { sx: { fontSize: 22, color: '#1976d2' } })}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b' }}>
                    {c.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" mb={2} sx={{ fontSize: '0.8rem' }}>
                  {c.desc}
                </Typography>
                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  sx={{
                    background: "#1976d2",
                    textTransform: "none",
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    '&:hover': { background: '#1565c0' },
                  }}
                  onClick={() => navigate(c.action)}
                >
                  {c.label}
                </Button>
              </Card>
            ))}
          </Box>
        </Box>

        {/* ================= ANALYTICS ================= */}
          <Box mt={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#6a4bd8' }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
                Analytics Overview
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              <Card sx={{ p: 1.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(15,23,42,0.06)', border: '1.5px solid #f1f5f9' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#1e293b', fontSize: '0.85rem' }}>
                  Monthly Sales
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                    <LineChart 
                    data={analytics.monthlySales.map((m) => ({
                            month: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m._id - 1] || `M${m._id}`,
                            sales: m.totalSales,
                          }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={currencyFormatter} />
                      <Tooltip formatter={(value) => currencyFormatter(value)} />
                      <Line type="monotone" dataKey="sales" stroke="#ff9900" />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

              <Card sx={{ p: 1.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(15,23,42,0.06)', border: '1.5px solid #f1f5f9' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#1e293b', fontSize: '0.85rem' }}>
                  Daily Revenue
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={analytics.weeklyRevenue}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis tickFormatter={currencyFormatter} />
                      <Tooltip formatter={(value) => currencyFormatter(value)} />
                      <Bar dataKey="amount" fill="#1976d2" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

              <Card sx={{ p: 1.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(15,23,42,0.06)', border: '1.5px solid #f1f5f9' }}>
                <Typography sx={{ fontWeight: 600, mb: 1, color: '#1e293b', fontSize: '0.85rem' }}>
                  Top Selling Products
                </Typography>
                <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={analytics.topProducts}
                        dataKey="orders"
                        nameKey="name"
                        outerRadius={100}
                        innerRadius={60}
                      >
                        {analytics.topProducts.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend formatter={(name) => name.length > 12 ? name.slice(0, 12) + '…' : name} />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
            </Box>
          </Box>
      </Box>
      )}
    </Box>
  );
};

export default VendorDashboard;